import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const FoodManagement = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedDay, setSelectedDay] = useState('all');
  const [selectedMealType, setSelectedMealType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    meal_type: 'breakfast',
    food_category: 'veg',
    item_name: '',
    item_icon: '🍽️',
    description: '',
    is_available: 1,
    display_order: 0
  });

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, selectedDay, selectedMealType, selectedCategory, selectedAvailability, searchQuery]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/mess-menu`);
      const data = await response.json();
      if (data.success) {
        setMenuItems(data.items || []);
      } else {
        setError('Failed to load menu items');
      }
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...menuItems];

    // Filter by day
    if (selectedDay !== 'all') {
      filtered = filtered.filter(item => item.day_of_week === selectedDay);
    }

    // Filter by meal type
    if (selectedMealType !== 'all') {
      filtered = filtered.filter(item => item.meal_type === selectedMealType);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => {
        // Handle both old format (food_category) and new format (is_vegetarian)
        if (item.food_category) {
          return item.food_category === selectedCategory;
        } else {
          const isVeg = item.is_vegetarian === 1;
          return selectedCategory === 'veg' ? isVeg : !isVeg;
        }
      });
    }

    // Filter by availability
    if (selectedAvailability !== 'all') {
      const isAvailable = selectedAvailability === 'available' ? 1 : 0;
      filtered = filtered.filter(item => item.is_available === isAvailable);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        (item.item_name && item.item_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.menu_item && item.menu_item.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredItems(filtered);
  };

  const handleAdd = async () => {
    try {
      const response = await fetch(`${API_URL}/mess-menu`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        fetchMenuItems();
        setShowAddModal(false);
        resetForm();
      } else {
        alert(data.message || 'Failed to add item');
      }
    } catch (err) {
      console.error('Error adding item:', err);
      alert('Failed to add item');
    }
  };

  const handleEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/mess-menu/${currentItem.menu_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        fetchMenuItems();
        setShowEditModal(false);
        resetForm();
      } else {
        alert(data.message || 'Failed to update item');
      }
    } catch (err) {
      console.error('Error updating item:', err);
      alert('Failed to update item');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/mess-menu/${currentItem.menu_id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchMenuItems();
        setShowDeleteModal(false);
        setCurrentItem(null);
      } else {
        alert(data.message || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    }
  };

  const toggleAvailability = async (item) => {
    try {
      const response = await fetch(`${API_URL}/mess-menu/${item.menu_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...item, is_available: item.is_available ? 0 : 1 })
      });
      const data = await response.json();
      if (data.success) {
        fetchMenuItems();
      }
    } catch (err) {
      console.error('Error toggling availability:', err);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (item) => {
    setCurrentItem(item);
    setFormData({
      meal_type: item.meal_type,
      food_category: item.food_category,
      item_name: item.item_name,
      item_icon: item.item_icon || '🍽️',
      description: item.description || '',
      is_available: item.is_available,
      display_order: item.display_order || 0
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (item) => {
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      meal_type: 'breakfast',
      food_category: 'veg',
      item_name: '',
      item_icon: '🍽️',
      description: '',
      is_available: 1,
      display_order: 0
    });
    setCurrentItem(null);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const getMealTypeColor = (type) => {
    switch(type) {
      case 'breakfast': return '#FF9800';
      case 'lunch': return '#4CAF50';
      case 'dinner': return '#2196F3';
      default: return '#666';
    }
  };

  const getCategoryBadge = (category) => {
    return category === 'veg' ? 
      <span className="badge badge-veg">🌱 Veg</span> :
      <span className="badge badge-nonveg">🍖 Non-Veg</span>;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid #f3f3f3',
          borderTop: '6px solid #52ab98',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{
          color: '#52ab98',
          fontSize: '18px',
          fontWeight: '600'
        }}>Loading Menu Items...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: linear-gradient(135deg, #f4f7fa 0%, #ffffff 100%);
          overflow: hidden;
        }

        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #2b6777 0%, #1e4d5a 100%);
          color: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          transform: translateX(-100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 32px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
        }

        .sidebar-nav {
          flex: 1;
          padding: 24px 16px;
          overflow-y: auto;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 14px 16px;
          margin-bottom: 8px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        .menu-item-active {
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .menu-icon {
          font-size: 22px;
          width: 28px;
          text-align: center;
        }

        .menu-label {
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }

        .logout-section {
          padding: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logout-btn {
          width: 100%;
          padding: 14px;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .logout-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
          z-index: 999;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .top-header {
          background: white;
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: linear-gradient(135deg, #2b6777 0%, #1e4d5a 100%);
          border: none;
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(43, 103, 119, 0.3);
        }

        .menu-toggle:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(43, 103, 119, 0.4);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
        }

        .header-greeting {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .header-name {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 700;
          color: white;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
        }

        .user-avatar-header:hover {
          transform: scale(1.1);
        }

        .content-wrapper {
          flex: 1;
          overflow-y: auto;
          padding: 32px;
        }

        .content-area {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 32px;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .page-subtitle {
          font-size: 16px;
          color: #666;
        }

        .controls-section {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          margin-bottom: 24px;
        }

        .controls-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 16px;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82, 171, 152, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: #666;
        }

        .filter-group {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-label {
          font-size: 14px;
          font-weight: 600;
          color: #666;
        }

        .filter-select {
          padding: 10px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
        }

        .btn-primary {
          padding: 12px 24px;
          background: linear-gradient(135deg, #52ab98 0%, #3d8b7a 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(82, 171, 152, 0.3);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(82, 171, 152, 0.4);
        }

        .stats-row {
          display: flex;
          gap: 16px;
          margin-top: 16px;
        }

        .stat-badge {
          padding: 8px 16px;
          background: linear-gradient(135deg, #f4f7fa 0%, #ffffff 100%);
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          border: 2px solid #e0e0e0;
        }

        .menu-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }

        .menu-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .menu-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
        }

        .menu-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .menu-card-icon {
          font-size: 48px;
          line-height: 1;
        }

        .menu-card-badges {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-end;
        }

        .meal-type-badge {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          color: white;
          text-transform: capitalize;
        }

        .badge {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          display: inline-block;
        }

        .badge-veg {
          background: #4CAF50;
          color: white;
        }

        .badge-nonveg {
          background: #f44336;
          color: white;
        }

        .menu-card-title {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .menu-card-description {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 16px;
          min-height: 44px;
        }

        .menu-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 1px solid #f0f0f0;
        }

        .availability-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.3s;
        }

        .availability-toggle:hover {
          background: #f4f7fa;
        }

        .toggle-switch {
          width: 44px;
          height: 24px;
          background: #ccc;
          border-radius: 12px;
          position: relative;
          transition: all 0.3s;
        }

        .toggle-switch.active {
          background: #4CAF50;
        }

        .toggle-slider {
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: all 0.3s;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .toggle-switch.active .toggle-slider {
          left: 22px;
        }

        .availability-text {
          font-size: 13px;
          font-weight: 600;
          color: #666;
        }

        .card-actions {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: all 0.3s;
        }

        .btn-edit {
          background: #2196F3;
          color: white;
        }

        .btn-edit:hover {
          background: #1976D2;
          transform: scale(1.1);
        }

        .btn-delete {
          background: #f44336;
          color: white;
        }

        .btn-delete:hover {
          background: #d32f2f;
          transform: scale(1.1);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 20px;
          padding: 32px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 700;
          color: #2b6777;
        }

        .modal-close {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          background: #f4f7fa;
          cursor: pointer;
          font-size: 20px;
          color: #666;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: #e0e0e0;
          transform: rotate(90deg);
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 15px;
          transition: all 0.3s;
          font-family: inherit;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82, 171, 152, 0.1);
        }

        .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .icon-picker {
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 8px;
          max-height: 200px;
          overflow-y: auto;
          padding: 12px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
        }

        .icon-option {
          font-size: 24px;
          padding: 8px;
          border-radius: 8px;
          cursor: pointer;
          text-align: center;
          transition: all 0.3s;
          border: 2px solid transparent;
        }

        .icon-option:hover {
          background: #f4f7fa;
        }

        .icon-option.selected {
          background: #52ab98;
          border-color: #3d8b7a;
          transform: scale(1.1);
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #f0f0f0;
        }

        .btn-secondary {
          padding: 12px 24px;
          background: #f4f7fa;
          color: #666;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }

        .btn-danger {
          padding: 12px 24px;
          background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
        }

        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(244, 67, 54, 0.4);
        }

        .delete-modal-content {
          text-align: center;
          padding: 20px;
        }

        .delete-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .delete-text {
          font-size: 18px;
          color: #666;
          margin-bottom: 8px;
        }

        .delete-item-name {
          font-size: 20px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 16px;
        }

        .delete-warning {
          font-size: 14px;
          color: #f44336;
          padding: 12px;
          background: #ffebee;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
        }

        .empty-icon {
          font-size: 80px;
          margin-bottom: 24px;
          opacity: 0.5;
        }

        .empty-title {
          font-size: 24px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 12px;
        }

        .empty-text {
          font-size: 16px;
          color: #666;
          margin-bottom: 24px;
        }

        /* Responsive Design */
        @media (min-width: 1024px) {
          .sidebar {
            transform: translateX(0);
            position: relative;
          }

          .close-btn {
            display: none;
          }

          .overlay {
            display: none;
          }

          .menu-toggle {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 100%;
            max-width: 320px;
          }

          .top-header {
            padding: 16px 20px;
          }

          .content-wrapper {
            padding: 20px;
          }

          .menu-grid {
            grid-template-columns: 1fr;
          }

          .controls-row {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            min-width: 100%;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .page-title {
            font-size: 24px;
          }

          .modal {
            padding: 24px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">
                  {userInfo?.name || 'Mess Manager'}
                </div>
                <div className="user-role-sidebar">Mess Manager Portal</div>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <MenuItem emoji="🏠" label="Dashboard" route="hostel-mess-dashboard" />
            <MenuItem emoji="🍽️" label="Food Management" active={true} route="hostel-mess-food" />
            <MenuItem emoji="📊" label="Daily Report" route="hostel-mess-foodimage" />
          </nav>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Food Management</div>
                <div className="header-name">Menu Items Control</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'M'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="content-area">
              <div className="page-header">
                <h1 className="page-title">Food Management 🍽️</h1>
                <p className="page-subtitle">
                  Manage your mess menu items - add, edit, or remove dishes
                </p>
              </div>

              <div className="controls-section">
                <div className="controls-row">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search menu items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>                  <div className="filter-group">
                    <span className="filter-label">Day:</span>
                    <select
                      className="filter-select"
                      value={selectedDay}
                      onChange={(e) => setSelectedDay(e.target.value)}
                    >
                      <option value="all">All Days</option>
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>



                  <div className="filter-group">
                    <span className="filter-label">Meal:</span>
                    <select
                      className="filter-select"
                      value={selectedMealType}
                      onChange={(e) => setSelectedMealType(e.target.value)}
                    >
                      <option value="all">All Meals</option>
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                  <option value="snacks">Snacks</option>
                  <option value="dinner">Dinner</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <span className="filter-label">Type:</span>
                    <select
                      className="filter-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="veg">Vegetarian</option>
                      <option value="nonveg">Non-Vegetarian</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <span className="filter-label">Status:</span>
                    <select
                      className="filter-select"
                      value={selectedAvailability}
                      onChange={(e) => setSelectedAvailability(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                  </div>

                  <button className="btn-primary" onClick={openAddModal}>
                    <span>➕</span> Add New Item
                  </button>
                </div>

                <div className="stats-row">
                  <div className="stat-badge">
                    Total Items: {menuItems.length}
                  </div>
                  <div className="stat-badge">
                    Showing: {filteredItems.length}
                  </div>
                  <div className="stat-badge">
                    Available: {menuItems.filter(i => i.is_available).length}
                  </div>
                </div>
              </div>

              {filteredItems.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🍽️</div>
                  <h2 className="empty-title">No menu items found</h2>
                  <p className="empty-text">
                    {searchQuery || selectedMealType !== 'all' || selectedCategory !== 'all'
                      ? 'Try adjusting your filters or search query'
                      : 'Start by adding your first menu item'}
                  </p>
                  {menuItems.length === 0 && (
                    <button className="btn-primary" onClick={openAddModal}>
                      <span>➕</span> Add First Item
                    </button>
                  )}
                </div>
              ) : (
                <div className="menu-grid">
                  {filteredItems.map((item) => (
                    <div key={item.menu_id} className="menu-card">
                      <div className="menu-card-header">
                        <div className="menu-card-icon">{item.item_icon || '🍽️'}</div>
                        <div className="menu-card-badges">
                          <div
                            className="meal-type-badge"
                            style={{ backgroundColor: getMealTypeColor(item.meal_type) }}
                          >
                            {item.meal_type}
                          </div>
                          {getCategoryBadge(item)}
                        </div>
                      </div>
                      <h3 className="menu-card-title">{item.item_name || item.menu_item}</h3>
                      <p className="menu-card-description">
                        {item.description || 'No description available'}
                      </p>
                      <div className="menu-card-footer">
                        <div
                          className="availability-toggle"
                          onClick={() => toggleAvailability(item)}
                        >
                          <div className={`toggle-switch ${item.is_available ? 'active' : ''}`}>
                            <div className="toggle-slider"></div>
                          </div>
                          <span className="availability-text">
                            {item.is_available ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                        <div className="card-actions">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => openEditModal(item)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => openDeleteModal(item)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Menu Item</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                ✕
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Item Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Poha, Idli, Dosa"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              />
            </div>

<div className="form-group">
              <label className="form-label">Day of Week *</label>
              <select
                className="form-select"
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Meal Type *</label>
                <select
                  className="form-select"
                  value={formData.meal_type}
                  onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="snacks">Snacks</option>
                  <option value="dinner">Dinner</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Food Category *</label>
                <select
                  className="form-select"
                  value={formData.food_category}
                  onChange={(e) => setFormData({ ...formData, food_category: e.target.value })}
                >
                  <option value="veg">Vegetarian</option>
                  <option value="nonveg">Non-Vegetarian</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                placeholder="Brief description of the dish..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Icon (Select an emoji)</label>
              <div className="icon-picker">
                {['🍚', '🥘', '🫓', '🍲', '🍛', '🧈', '🥬', '🍳', '🍗', '🐟', '🥚', '🍖', '🍞', '🥙', '🌮', '🍕', '🍝', '🥗', '🍜', '🍱', '🍣', '🍤', '🥟', '🍙', '🧆', '🥪', '🌯', '🥓', '🍔', '🌭', '🥞', '🧇'].map(icon => (
                  <div
                    key={icon}
                    className={`icon-option ${formData.item_icon === icon ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, item_icon: icon })}
                  >
                    {icon}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Availability</label>
                <select
                  className="form-select"
                  value={formData.is_available}
                  onChange={(e) => setFormData({ ...formData, is_available: parseInt(e.target.value) })}
                >
                  <option value={1}>Available</option>
                  <option value={0}>Unavailable</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAdd}>
                <span>➕</span> Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Menu Item</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Item Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Poha, Idli, Dosa"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              />
            </div>

<div className="form-group">
              <label className="form-label">Day of Week *</label>
              <select
                className="form-select"
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Meal Type *</label>
                <select
                  className="form-select"
                  value={formData.meal_type}
                  onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="snacks">Snacks</option>
                  <option value="dinner">Dinner</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Food Category *</label>
                <select
                  className="form-select"
                  value={formData.food_category}
                  onChange={(e) => setFormData({ ...formData, food_category: e.target.value })}
                >
                  <option value="veg">Vegetarian</option>
                  <option value="nonveg">Non-Vegetarian</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                placeholder="Brief description of the dish..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Icon (Select an emoji)</label>
              <div className="icon-picker">
                {['🍚', '🥘', '🫓', '🍲', '🍛', '🧈', '🥬', '🍳', '🍗', '🐟', '🥚', '🍖', '🍞', '🥙', '🌮', '🍕', '🍝', '🥗', '🍜', '🍱', '🍣', '🍤', '🥟', '🍙', '🧆', '🥪', '🌯', '🥓', '🍔', '🌭', '🥞', '🧇'].map(icon => (
                  <div
                    key={icon}
                    className={`icon-option ${formData.item_icon === icon ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, item_icon: icon })}
                  >
                    {icon}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Availability</label>
                <select
                  className="form-select"
                  value={formData.is_available}
                  onChange={(e) => setFormData({ ...formData, is_available: parseInt(e.target.value) })}
                >
                  <option value={1}>Available</option>
                  <option value={0}>Unavailable</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleEdit}>
                <span>💾</span> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && currentItem && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Menu Item</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                ✕
              </button>
            </div>

            <div className="delete-modal-content">
              <div className="delete-icon">🗑️</div>
              <p className="delete-text">Are you sure you want to delete</p>
              <p className="delete-item-name">{currentItem.item_name || currentItem.menu_item}?</p>
              <div className="delete-warning">
                ⚠️ This action cannot be undone. The menu item will be permanently removed.
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDelete}>
                <span>🗑️</span> Delete Item
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FoodManagement;